import { useFormik } from "formik";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import * as React from "react";
import { useContext } from "react";
import { useFragment, useMutation } from "react-relay";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import { type editNodeForm_EditNodeCoreMutation } from "@relay/editNodeForm_EditNodeCoreMutation.graphql";
import { type editNodeForm_TreeNodeFragment$key } from "@relay/editNodeForm_TreeNodeFragment.graphql";
import { SHORT_DESCRIPTION_MAX_LENGTH } from "./edit-node-form.const";
import { EDIT_NODE_CORE_MUTATION, TREE_NODE_FRAGMENT } from "./edit-node-form.graphql";
import { type EditNodeFormProps, type EditNodeFormState } from "./edit-node-form.types";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { MemoizedRichTextEditor } from "../../../../components/rich-text-editor";
import { RichTextEditorToolbar } from "../../../../components/rich-text-editor/rich-text-editor.const";
import { ValidatedField } from "../../../../components/ValidatedField";
import { InstructorsMultiSelect } from "../../../../features/instructors/InstructorsMultiSelect";
import {
	OfferFileSelectionField as FileSelectionField,
	type FileV2,
} from "../../../../features/offer-files/offer-file-selection-field";
import { useHasPermissions } from "../../../../hooks/use-has-permissions";
import {
	addEditedFormToEditedFormsArray,
	removeEditedFormFromEditedFormsArray,
} from "../../../../store/slices/CoreSlice";
import { useTypedDispatch } from "../../../../store/store.redux";
import { TreeSelectionContext } from "../TreeSelectionContext";

export const EditNodeForm = ({ treeNodeFragmentRef }: EditNodeFormProps) => {
	const { t } = useTranslation(["common", "validation"]);
	const node = useFragment<editNodeForm_TreeNodeFragment$key>(
		TREE_NODE_FRAGMENT,
		treeNodeFragmentRef,
	);
	const dispatch = useTypedDispatch();
	const { treeId } = useContext(TreeSelectionContext);
	const [editNodeCore, isEditingNodeCore] =
		useMutation<editNodeForm_EditNodeCoreMutation>(EDIT_NODE_CORE_MUTATION);

	const formId = "AcademiesEditNodeForm";
	const formik = useFormik<EditNodeFormState>({
		enableReinitialize: true,
		initialValues: {
			title: node.structureDefinition.title,
			description: node.description ?? undefined,
			shortDescription: node.shortDescription ?? undefined,
			image: node.image
				? ({
						id: node.image.id,
						name: node.image.name,
						url: node.image.url,
				  } satisfies FileV2)
				: undefined,
			instructors: node.instructors.map((i) => i.superId) ?? null,
		},
		validationSchema: Yup.object().shape({
			title: Yup.string().required(t("validation:titleRequired")),
			shortDescription: Yup.string().max(
				SHORT_DESCRIPTION_MAX_LENGTH,
				t("validation:shortDescriptionMax", { max: SHORT_DESCRIPTION_MAX_LENGTH }),
			),
		}),

		onSubmit: (values, { setSubmitting }) => {
			editNodeCore({
				variables: {
					input: {
						nodeId: node.id,
						title: values.title,
						description: values.description,
						shortDescription: values.shortDescription,
						imageId: values.image?.id,
						instructorIds: values.instructors || [],
					},
				},
				updater: (store) => {
					// After any save the backend resets the tree's status to Draft, but the
					// mutation only returns the edited node. Manually update the root node's
					// status so the publish button reappears without a page refresh.
					const rootRecord = store.get(treeId);
					if (rootRecord) rootRecord.setValue("Draft", "status");
				},
				onError: () => {
					toast.error(t("errors.savingCourse"));
				},
				onCompleted: () => {
					void formik.setTouched({});
					setSubmitting(false);
					dispatch(removeEditedFormFromEditedFormsArray({ form: formId }));
				},
			});
		},
	});

	const handleFormEdited = React.useCallback(
		() => dispatch(addEditedFormToEditedFormsArray({ form: formId })),
		[dispatch],
	);

	const canUpload = useHasPermissions(["UserInAccountPermission_Nodes_UpdateNodes"]);
	const canDelete = useHasPermissions("onlyOwnerOfRoot");

	const disabled = Object.entries(formik.touched).length === 0 || isEditingNodeCore;

	return (
		<Card className="mb-2">
			<h2>{t("forms.folderSettings")}</h2>
			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField<EditNodeFormState, string>
					name={"title"}
					label={t("general.title")}
					component={DefaultTextFieldComponent}
					onChange={handleFormEdited}
					formikConfig={formik}
				/>
				<ValidatedField<EditNodeFormState, string>
					name={"description"}
					label={t("forms.description")}
					component={(props) => (
						<MemoizedRichTextEditor
							{...props}
							height={200}
							toolbar={RichTextEditorToolbar.WithLists}
						/>
					)}
					onChange={handleFormEdited}
					formikConfig={formik}
				/>
				<ValidatedField<EditNodeFormState, string[]>
					name={"instructors"}
					label={t("forms.instructors")}
					helpText={t("forms.selectInstructors")}
					onChange={handleFormEdited}
					component={InstructorsMultiSelect}
					formikConfig={formik}
				/>
				<ValidatedField<EditNodeFormState, string>
					name={"shortDescription"}
					label={t("forms.shortDescription")}
					helpText={t("forms.shortDescriptionHelpText", {
						max: SHORT_DESCRIPTION_MAX_LENGTH,
						current: formik.values.shortDescription?.length ?? 0,
					})}
					component={DefaultTextFieldComponent}
					onChange={handleFormEdited}
					formikConfig={formik}
				/>

				<ValidatedField<EditNodeFormState, FileV2>
					name={"image"}
					label={t("forms.image")}
					onChange={handleFormEdited}
					component={({ fieldName, fieldValue, updateField, onChange }) => {
						return (
							<FileSelectionField
								name={fieldName}
								selectedFile={fieldValue}
								setSelectedFile={updateField}
								filterByFileTypes={["image/png", "image/jpg", "image/jpeg"]}
								onChange={onChange}
								canUploadFiles={canUpload}
								canDeleteFiles={canDelete}
							/>
						);
					}}
					formikConfig={formik}
				/>

				<Button
					disabled={disabled}
					type="submit"
					label={t("actions.save")}
					className="p-mt-2"
				/>
			</form>
		</Card>
	);
};
